import { usePricingRights } from '@util/PricingRightsHook';
import React, { PropsWithChildren } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import PricingRoom from '@app/model/pricing-automation/PricingRoom';
import { useQuery } from '@tanstack/react-query';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';

type PricingRoomsInventoryContextType = {
	pricingRooms: PricingRoom[],
	totalRooms: number | undefined,
	findTotalRoomsByRoomName: (roomName: string) => number | undefined,
}

export const PricingRoomsInventoryContext = React.createContext<PricingRoomsInventoryContextType>({
	pricingRooms: [],
	totalRooms: undefined,
	findTotalRoomsByRoomName: () => undefined,
});

const normalizeRoomName = (roomName: string) => roomName.trim().toLowerCase();

const parseNumberOfRooms = (pricingRoom: PricingRoom) => {
	const value = Number(pricingRoom.numberOfRooms);
	return Number.isFinite(value) && value > 0 ? value : undefined;
};

const PricingRoomsInventoryProvider: React.FunctionComponent<PropsWithChildren<{
	companyHotel: CompanyHotel,
}>> = ({ companyHotel, children }) => {
	const { canChangePrices } = usePricingRights(companyHotel);
	const { data, isSuccess } = useQuery({
		queryKey: PricingAutomationQueryKeys.pricingRooms(companyHotel.id),
		queryFn: () => PricingAutomationService.findCompanyHotelPricingRooms(companyHotel.id),
		enabled: canChangePrices,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: true,
		staleTime: Infinity,
	});

	const pricingRooms = isSuccess ? data : [];

	const value = React.useMemo<PricingRoomsInventoryContextType>(() => {
		const roomTotalsByName = pricingRooms.reduce((acc, pricingRoom) => {
			const numberOfRooms = parseNumberOfRooms(pricingRoom);
			if (numberOfRooms === undefined) return acc;

			const roomName = normalizeRoomName(pricingRoom.roomName);
			acc[roomName] = (acc[roomName] ?? 0) + numberOfRooms;
			return acc;
		}, {} as { [roomName: string]: number });

		const totalRooms = Object.values(roomTotalsByName).reduce((acc, numberOfRooms) => acc + numberOfRooms, 0);

		return {
			pricingRooms,
			totalRooms: totalRooms > 0 ? totalRooms : undefined,
			findTotalRoomsByRoomName: (roomName: string) => roomTotalsByName[normalizeRoomName(roomName)],
		};
	}, [pricingRooms]);

	return <PricingRoomsInventoryContext.Provider value={value}>{children}</PricingRoomsInventoryContext.Provider>;
};

export default PricingRoomsInventoryProvider;
